import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// mui
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { InputAdornment } from '@mui/material';

// component
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import TopBackButton from '@/component/common/top-back-button';
import AlertDialog from '@/component/common/sf-alert-dialog';
import { qr_camera_call } from '@/component/linkMobile/qrCam';
// lib & store
import { SFPalette } from '@/lib/color';
import React from 'react';
import { useOutLinkInfo, useOutLinkInfoActions, useSendInfoActions } from '@/store/zustand/store';
import { weiToEth } from '@/lib/web3/utils';
// img
import ImgCam from '@/assets/images/camera.svg';
import { useWeb3 } from '@/hooks/use-web3';
import { useWeb3AuthConnect, useWeb3AuthUser } from '@web3auth/modal/react';
import { localGetStorage } from '@/lib/storage';

type FormDataType = {
  address: `0x${string}` | string;
  toAddr: string | `0x${string}`;
};

const schema = Joi.object({
  address: Joi.string().required().messages({
    'string.empty': '주소를 입력해 주세요.',
  }),
  toAddr: Joi.string().required().messages({
    'string.empty': '받는 주소를 입력해 주세요.',
  }),
});

export default function SendForm() {
  const navigate = useNavigate();
  const outLinkInfo = useOutLinkInfo();
  const { setSendInfo } = useSendInfoActions();
  const { setOutLinkInfo } = useOutLinkInfoActions();

  // 뒤로가기 시 outLinkInfo 초기화 (WalletMain에서 다시 리다이렉트 방지)
  const handleBack = () => {
    setOutLinkInfo({
      path: '',
      toAddr: '',
      amount: '',
      symbol: '',
      chain: '',
    });
  };

  const { isConnected } = useWeb3AuthConnect();
  const { address } = useWeb3();
  const { userInfo, getUserInfo } = useWeb3AuthUser();

  const [isMetamask, setIsMetamask] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormDataType>({
    resolver: joiResolver(schema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isConnected || !address) {
      return;
    }
    setValue('address', address);

    return () => {};
  }, [isConnected, address]);
  useEffect(() => {
    // 1. 먼저 localStorage에서 빠르게 확인
    const snsAddress = localGetStorage('snsAddress');

    if (!snsAddress || (typeof snsAddress === 'object' && Object.keys(snsAddress).length === 0)) {
      // 2. localStorage에 데이터가 없을 때만 getUserInfo() 호출
      (async () => {
        const re = await getUserInfo();
        console.log(re);
        if (!re || (typeof re === 'object' && Object.keys(re).length === 0)) {
          setIsMetamask(true);
        }
      })();
    } else {
      // typeOfLogin이 'external'인 경우 MetaMask로 간주
      if (snsAddress.typeOfLogin === 'external') {
        setIsMetamask(true);
      } else {
        setIsMetamask(false);
      }
    }
  }, []);
  // useEffect(() => {
  //   if (!fromAddr) {
  //     alert('보내는 주소가 없습니다.');
  //     return;
  //   }

  //   setValue('address', fromAddr);
  //   setValue('toAddr', '');
  // }, [setValue, fromAddr]);

  // 외부 링크에서 전송을 요청 할 때
  useEffect(() => {
    if (outLinkInfo.toAddr) {
      setValue('toAddr', outLinkInfo.toAddr);
    }
    return () => {};
  }, []);

  window.appInterFace = (val, data) => {
    console.log(`in camCallback`);
    appInterFace(val);
  };
  const appInterFace = (val: any) => {
    if (!val.startsWith('0x') && val.startsWith('ethereum:')) {
      val = val.substring(9);
    }
    setValue('toAddr', val);
  };

  const onSubmit = (data: FormDataType) => {
    // setOpen(true);
    const payload = {
      from: data.address,
      to: data.toAddr,
      value: '',
    };
    setSendInfo(payload);

    navigate('/send');
  };

  return (
    <Box>
      <Container
        maxWidth="sm"
        sx={{
          minHeight: 812,
          position: 'relative',
        }}
      >
        <TopBackButton
          title="받는 사람"
          boxProps={{
            mb: outLinkInfo.amount ? 2 : 4,
          }}
          backPath="/wallet"
          onClick={handleBack}
        />
        {/* 결제 요청 안내 */}
        {outLinkInfo.amount && outLinkInfo.symbol && (
          <Box
            sx={{
              bgcolor: 'primary.main',
              p: 1.5,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="white" textAlign="center">
              결제 요청: {weiToEth(outLinkInfo.amount)} {outLinkInfo.symbol}
            </Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="body1" mb={1}>
            <b>From</b>
          </Typography>
          <TextField
            fullWidth
            error={!!errors.address}
            helperText={errors.address?.message}
            {...register('address')}
            disabled
          />
          <Box mb={5} />
          <Typography variant="body1" mb={1}>
            <b>TO</b>
          </Typography>
          <TextField
            fullWidth
            error={!!errors.toAddr}
            helperText={errors.toAddr?.message}
            {...register('toAddr')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => {
                      qr_camera_call();
                    }}
                  >
                    <ImgCam />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <Box position="absolute" bottom={350} left={0} width="100%">
            <Container maxWidth="sm">
              <Stack direction="row" gap={1} mb={3}>
                {isMetamask ? (
                  <Box sx={{ mt: 3, mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'center',
                        lineHeight: 1.6,
                        color: 'text.secondary',
                        mb: 3,
                      }}
                    >
                      [ 공지 ]
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'left',
                        lineHeight: 1.6,
                        color: 'text.secondary',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      Metamask 등 외부지갑 전송서비스는 준비중입니다.{'\n'}
                      SNS 지갑을 이용하시거나 외부지갑에서 직접 전송해주세요.
                    </Typography>
                  </Box>
                ) : (
                  <Button variant="outlined" size="large" fullWidth type="submit">
                    다음
                  </Button>
                )}
                {/* <Button variant="outlined" size="large" fullWidth type="submit">
                  다음
                </Button> */}
              </Stack>
            </Container>
          </Box>
        </form>
      </Container>
      {/* <AlertDialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Typography variant="h6" sx={{ wordBreak: 'keep-all' }} textAlign="center" fontWeight={500}>
          이 콜렉티블의 소유자가 아니므로 NFT를 불러올 수 없습니다.
        </Typography>
      </AlertDialog> */}
    </Box>
  );
}
